import type {
  AdminStatus,
  ClientErrorReport,
  EngagementSummary,
  ErrorLogEntry,
  IcecastTestResult,
  LanguageRequest,
  MetricEvent,
  PublicConfig,
  PublicStatus,
  RadioConfig,
  StreamerStatus,
  TranslatorMetadata,
  TranslatorOverview,
  TranslatorSession,
} from '../shared/types';

export type AdminSession = { oidcEnabled: boolean; tokenEnabled: boolean; authenticated: boolean; name?: string; csrf?: string };
let adminCsrf = '';

const TOKEN_KEY = 'radiostation.adminToken';

export const adminToken = {
  get: () => sessionStorage.getItem(TOKEN_KEY) || '',
  set: (value: string) => sessionStorage.setItem(TOKEN_KEY, value),
  clear: () => sessionStorage.removeItem(TOKEN_KEY),
};

async function request<T>(url: string, init?: RequestInit, token?: string): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: {
      accept: 'application/json',
      ...(adminCsrf && (url.startsWith('/api/admin/') || url === '/api/auth/logout') ? { 'x-csrf-token': adminCsrf } : {}),
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...(token ? { authorization: 'Bearer ' + token } : {}),
      ...init?.headers,
    },
  });
  const body = (await response.json().catch(() => ({}))) as { message?: string } & T;
  if (!response.ok) {
    const error = new Error(body.message || 'Request failed with HTTP ' + response.status);
    Object.assign(error, { status: response.status });
    throw error;
  }
  return body;
}

export const api = {
  adminSession: async () => {
    const result = await request<AdminSession>('/api/auth/session');
    adminCsrf = result.csrf || '';
    return result;
  },
  adminLogout: async () => {
    await request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' });
    adminCsrf = '';
  },
  publicConfig: () => request<PublicConfig>('/api/config'),
  publicStatus: () => request<PublicStatus>('/api/status'),
  translatorSession: (token: string) =>
    request<TranslatorSession>('/api/translator/' + encodeURIComponent(token)),
  saveTranslatorMetadata: (token: string, metadata: TranslatorMetadata) =>
    request<TranslatorMetadata>('/api/translator/' + encodeURIComponent(token) + '/metadata', {
      method: 'PUT',
      body: JSON.stringify(metadata),
    }),
  languageRequests: () => request<LanguageRequest[]>('/api/language-requests'),
  voteForLanguage: (languageCode: string) =>
    request<LanguageRequest>('/api/language-requests/' + encodeURIComponent(languageCode) + '/vote', {
      method: 'POST',
    }),
  trackMetric: (event: MetricEvent) =>
    request<{ ok: boolean }>('/api/metrics/events', {
      method: 'POST',
      body: JSON.stringify(event),
      keepalive: true,
    }),
  adminConfig: (token: string) => request<RadioConfig>('/api/admin/config', undefined, token),
  adminStatus: (token: string) => request<AdminStatus>('/api/admin/status', undefined, token),
  adminEngagement: (token: string) =>
    request<EngagementSummary>('/api/admin/engagement', undefined, token),
  adminErrors: (token: string) => request<ErrorLogEntry[]>('/api/admin/errors', undefined, token),
  adminTranslators: (token: string) =>
    request<TranslatorOverview>('/api/admin/translators', undefined, token),
  saveTopic: (token: string, topic: string) =>
    request<TranslatorOverview>('/api/admin/topic', {
      method: 'PUT',
      body: JSON.stringify({ topic }),
    }, token),
  regenerateTranslatorTokens: (token: string) =>
    request<TranslatorOverview>('/api/admin/translators/regenerate-tokens', { method: 'POST' }, token),
  disconnectTranslator: (token: string, mountPoint: string) =>
    request<{ ok: boolean }>('/api/admin/translators/' + encodeURIComponent(mountPoint) + '/disconnect', {
      method: 'POST',
    }, token),
  savePlayback: (token: string, enhancedPlayerEnabled: boolean) =>
    request<{ enhancedPlayerEnabled: boolean }>('/api/admin/playback', {
      method: 'PUT',
      body: JSON.stringify({ enhancedPlayerEnabled }),
    }, token),
  saveConfig: (token: string, config: RadioConfig) =>
    request<RadioConfig>('/api/admin/config', { method: 'PUT', body: JSON.stringify(config) }, token),
  obsAction: (token: string, action: 'start' | 'stop' | 'refresh') =>
    request<{ ok: boolean }>('/api/admin/obs/' + action, { method: 'POST' }, token),
  testIcecast: (token: string) =>
    request<IcecastTestResult>('/api/admin/test/icecast', { method: 'POST' }, token),
  testStreamer: (token: string) =>
    request<StreamerStatus>('/api/admin/test/streamer', { method: 'POST' }, token),
};

export function sendMetricBeacon(event: MetricEvent): void {
  const payload = new Blob([JSON.stringify(event)], { type: 'application/json' });
  if (!navigator.sendBeacon('/api/metrics/events', payload)) {
    void api.trackMetric(event).catch(() => undefined);
  }
}

export function errorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : 'Something went wrong.';
  reportClientError({
    message,
    ...(error instanceof Error && error.stack ? { stack: error.stack } : {}),
    source: 'caught client error',
    path: window.location.pathname,
  });
  return message;
}

const recentClientErrors = new Map<string, number>();

export function reportClientError(report: ClientErrorReport): void {
  const sanitized = {
    ...report,
    path: (report.path || window.location.pathname).replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi,
      '[uuid]',
    ),
  };
  const signature = sanitized.message + '|' + (sanitized.stack || '') + '|' + sanitized.path;
  const now = Date.now();
  if ((recentClientErrors.get(signature) || 0) > now - 10_000) return;
  recentClientErrors.set(signature, now);
  if (recentClientErrors.size > 100) {
    for (const [key, timestamp] of recentClientErrors) {
      if (timestamp < now - 60_000) recentClientErrors.delete(key);
    }
  }
  const payload = new Blob([JSON.stringify(sanitized)], { type: 'application/json' });
  if (!navigator.sendBeacon('/api/client-errors', payload)) {
    void fetch('/api/client-errors', {
      method: 'POST',
      body: JSON.stringify(sanitized),
      headers: { 'content-type': 'application/json' },
      keepalive: true,
    }).catch(() => undefined);
  }
}
